import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import GateCard from "@/components/GateCard";
import { fmtMoney, fmtPct } from "@/lib/format";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function EvidenceResults() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    endpoints.evidence().then((d) => {
      setData(d);
      setLoading(false);
    }).catch(() => {
      toast.error("Failed to load evidence");
      setLoading(false);
    });
  }, []);

  if (loading || !data) {
    return (
      <div className="container-hi py-10 text-[13px]" style={{ color: "var(--cream-muted)" }}>
        Loading evidence…
      </div>
    );
  }

  const evaluableParticipants = data.participants.filter((p) => p.evaluable && p.status !== "withdrawn");
  evaluableParticipants.sort((a, b) => (b.preservation_rate ?? 0) - (a.preservation_rate ?? 0));

  return (
    <div className="container-hi py-10" data-testid="evidence-page">
      <header>
        <div className="text-[11px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
          Evidence Results
        </div>
        <h1 className="font-display text-[44px] leading-[1.05] mt-2" style={{ color: "var(--cream)" }}>
          Frozen gates
        </h1>
        <p className="mt-3 text-[13px] max-w-2xl" style={{ color: "var(--cream-dim)" }}>
          The two Evidence gates for Stage 1 are fixed and computed dynamically from the sample cohort.
          Thresholds are not tuned to make the data look successful; they are the standard the program
          committed to before running.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <GateCard
          testId="ev-gate-adherence"
          label="Gate A · On-Time Adherence"
          subLabel="Median across behaviorally evaluable participants"
          value={data.gate_adherence.value}
          pass={data.gate_adherence.pass}
          thresholdLabel="Frozen threshold ≥ 70%"
        />
        <GateCard
          testId="ev-gate-preservation"
          label="Gate B · Preservation Success"
          subLabel="Share of evaluable participants with ≥80% Preservation Rate"
          value={data.gate_preservation.value}
          pass={data.gate_preservation.pass}
          thresholdLabel="Frozen threshold ≥ 60% of evaluable"
        />
      </section>

      <div
        className="mt-4 p-4 rounded-md text-[12px] leading-relaxed"
        style={{
          background: "rgba(201,168,76,0.05)",
          border: "1px solid rgba(201,168,76,0.25)",
          color: "var(--cream-dim)",
        }}
        data-testid="ev-gates-note"
      >
        <span className="font-mono-num text-[10px] tracking-widest uppercase mr-2" style={{ color: "var(--gold-light)" }}>
          Note
        </span>
        Core behavioral gates are necessary but not sufficient for advancement. Stage 1 also
        requires satisfactory safety/comprehension and technical/data-usability results before a
        Proceed decision.
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="ev-indicators">
        <Small label="Recruited" value={data.total_recruited} />
        <Small label="Activated" value={data.activated} />
        <Small label="Evaluable" value={data.evaluable_count} />
        <Small label="Preservation winners" value={`${data.preservation_success_count}/${data.evaluable_count}`} />
        <Small label="Commitments due" value={data.commitments_due} />
        <Small label="On time" value={data.commitments_on_time} />
        <Small label="Late/Recovered" value={data.commitments_late_recovered} />
        <Small label="Missed" value={data.commitments_missed} />
        <Small label="Trip expenses" value={fmtMoney(data.trip_expenses_total)} />
        <Small label="Non-trip leakage" value={fmtMoney(data.non_trip_leakage_total)} />
        <Small label="Preserved (cohort)" value={fmtMoney(data.preserved_total)} />
        <Small label="Behind" value={data.behind_count} />
      </section>

      <section className="mt-10" data-testid="ev-leaderboard">
        <h2 className="font-display text-[22px] mb-4" style={{ color: "var(--cream)" }}>Evaluable participants — preservation ranking</h2>
        <div className="surface overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest font-mono-num border-b" style={{ color: "var(--cream-muted)", borderColor: "var(--hairline)" }}>
            <div className="col-span-4">Participant</div>
            <div className="col-span-3">Adherence</div>
            <div className="col-span-3">Preservation</div>
            <div className="col-span-2 text-right">Preserved</div>
          </div>
          {evaluableParticipants.map((p) => {
            const passing = (p.preservation_rate ?? 0) >= 0.80;
            return (
              <Link
                key={p.id}
                to={`/participants/${p.id}`}
                className="grid grid-cols-12 px-6 py-4 items-center border-b row-hover text-[13px]"
                style={{ borderColor: "var(--hairline)" }}
                data-testid={`ev-row-${p.id}`}
              >
                <div className="col-span-4">
                  <div style={{ color: "var(--cream)" }}>{p.name}</div>
                  <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>{p.trip_destination}</div>
                </div>
                <div className="col-span-3 font-mono-num" style={{ color: "var(--cream)" }}>
                  {fmtPct(p.adherence_rate)}
                  <span className="text-[11px] ml-2" style={{ color: "var(--cream-muted)" }}>
                    {p.commitments_on_time}/{p.commitments_due}
                  </span>
                </div>
                <div className="col-span-3 flex items-center gap-2 font-mono-num" style={{ color: passing ? "var(--success)" : "var(--cream)" }}>
                  {fmtPct(p.preservation_rate)}
                  {passing && <span className="chip success">Meets gate</span>}
                </div>
                <div className="col-span-2 text-right font-mono-num" style={{ color: "var(--cream)" }}>{fmtMoney(p.preserved_savings)}</div>
              </Link>
            );
          })}
          {evaluableParticipants.length === 0 && (
            <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>No behaviorally evaluable participants yet.</div>
          )}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ev-non-behavioral">
        <NonBehavioralPanel
          testId="panel-safety"
          title="Safety & Comprehension"
          body="Participant safety and informed comprehension of the Evidence Program design must be assessed alongside the two behavioral gates. Satisfactory results here — not just Gate A and Gate B — are required before a Proceed decision."
        />
        <NonBehavioralPanel
          testId="panel-technical"
          title="Technical & Data Usability"
          body="Data quality, technical reliability, and the usability of behavioral signals produced by the program must be assessed. Satisfactory results here are also required before a Proceed decision, in addition to the two behavioral gates."
        />
      </section>

      <section className="mt-10 surface p-6" data-testid="ev-methodology">
        <h3 className="font-display text-[18px]" style={{ color: "var(--cream)" }}>Behavioral calculation notes</h3>
        <ul className="mt-3 space-y-2 text-[12px]" style={{ color: "var(--cream-dim)" }}>
          <li>• On-Time Commitment Adherence = on-time commitments ÷ commitments due, where on-time = ±3 days from schedule.</li>
          <li>• Preservation Rate = (Preserved Savings + Legitimate Trip Expenses) ÷ (Starting Designated + Confirmed Contributions).</li>
          <li>• Preserved Savings never falls below $0. Travel Goal Progress caps at target; amounts above target are separately identified.</li>
          <li>• Trip expenses reduce Preserved Savings without reducing Travel Goal Progress. Non-trip leakage reduces both.</li>
          <li>• A participant becomes evaluable after ≥28 days active OR ≥4 scheduled commitments due. Ambiguous activity stays pending until reviewed.</li>
        </ul>
      </section>
    </div>
  );
}

function Small({ label, value }) {
  return (
    <div className="surface p-4">
      <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>{label}</div>
      <div className="mt-2 font-mono-num text-[18px]" style={{ color: "var(--cream)" }}>{value}</div>
    </div>
  );
}

function NonBehavioralPanel({ title, body, testId }) {
  return (
    <div className="surface p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase font-mono-num" style={{ color: "var(--cream-muted)" }}>
            Non-Behavioral Requirement
          </div>
          <div className="mt-2 font-display text-[20px]" style={{ color: "var(--cream)" }}>
            {title}
          </div>
        </div>
        <span
          className="chip"
          style={{
            background: "var(--surface-3)",
            color: "var(--cream-dim)",
            border: "1px solid var(--hairline-strong)",
          }}
          data-testid={`${testId}-status`}
        >
          Not assessed in this demo
        </span>
      </div>
      <p className="mt-4 text-[12px] leading-relaxed" style={{ color: "var(--cream-dim)" }}>
        {body}
      </p>
    </div>
  );
}
